import React, { useState, Fragment, useEffect, useRef, useCallback } from 'react';
import { HiOutlineDeviceMobile, HiOutlineEye, HiOutlineTrash } from 'react-icons/hi';
import { FaUsers, FaLaptop, FaClock, FaFileExcel } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import { toastService } from '../../../services/toast';
import DeleteConfirmModal from '../../../components/common/DeleteConfirmModal';
import { Dialog, Transition } from '@headlessui/react'; 
import { XIcon } from '@heroicons/react/solid';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { deviceService } from '../../../services/deviceService';
import { SearchIcon } from '@heroicons/react/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/outline';

const formatDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '--';
  const date = new Date(dateTimeStr);
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const period = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const year = date.getFullYear();
  
  return `${day}/${month}/${year} ${hours12}:${minutes} ${period}`;
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return new Intl.DateTimeFormat('ar', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(date);
};

// ===== Skeleton Component =====
const UserDevicesSkeleton = () => {
  return (
    <div className="space-y-6 p-6 animate-pulse">
      {/* Stats Cards Skeleton - بالضبط نفس مقاسات الكروت الأصلية */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((item) => (
          <div key={item} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 h-[120px]">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gray-200 dark:bg-gray-700 rounded-full w-14 h-14"></div>
              <div className="flex-1">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-32 mb-2"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Filter Section Skeleton - بالضبط نفس مقاسات قسم البحث الأصلي */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-28 mb-2"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          </div>
          <div>
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-28 mb-2"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          </div>
          <div>
            <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-28 mb-2"></div>
            <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
          </div>
        </div>
      </div>

      {/* Table Skeleton - بالضبط نفس مقاسات الجدول الأصلي */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
          <div className="flex space-x-2 rtl:space-x-reverse">
            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                {/* بالضبط نفس عدد الأعمدة في الجدول الأصلي */}
                {[1, 2, 3, 4, 5, 6, 7].map((item) => (
                  <th key={item} scope="col" className="px-6 py-3">
                    <div className="h-5 bg-gray-200 dark:bg-gray-600 rounded w-24"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {/* بالضبط نفس عدد الصفوف في الجدول الأصلي */}
              {[1, 2, 3, 4, 5].map((row) => (
                <tr key={row}>
                  {[1, 2, 3, 4, 5, 6, 7].map((cell) => (
                    <td key={`${row}-${cell}`} className="px-6 py-4 whitespace-nowrap">
                      <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-full max-w-[120px]"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-between items-center">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
            <div className="flex space-x-2 rtl:space-x-reverse">
              {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="h-8 w-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const UserDevices = () => {
  const { hasPermission } = usePermissions();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [selectedUser, setSelectedUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteModalConfig, setDeleteModalConfig] = useState({
    isOpen: false,
    deviceId: null,
    userId: null
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState({ total_users: 0, total_devices: 0, avg_devices_per_user: 0 });
  const searchTimeoutRef = useRef(null);

  // جلب إحصائيات الأجهزة
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await deviceService.getDeviceStats();
        setStats(data);
      } catch (err) {
        console.error('Error fetching device stats:', err);
      }
    };
    fetchStats();
  }, []);

  // جلب البيانات الأولية
  useEffect(() => {
    const fetchInitialUsers = async () => {
      setError(null);
      try {
        const data = await deviceService.searchUsers({ per_page: 20 });
        setUsers(data.data || []);
        setPagination(data.pagination || null);
      } catch (err) {
        console.error('Error fetching users:', err);
        setError('حدث خطأ أثناء جلب بيانات المستخدمين');
        toastService.error('حدث خطأ أثناء جلب بيانات المستخدمين');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialUsers();
  }, []);

  // تنظيف التايمر عند إزالة المكون
  useEffect(() => {
    return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
  }, []);

  const fetchUsers = useCallback(async (page = 1, search = '') => {
    try {
      const params = { per_page: 20, page };
      if (search.trim()) params.search = search.trim();
      const data = await deviceService.searchUsers(params);
      setUsers(data.data || []);
      setPagination(data.pagination || null);
    } catch {
      setUsers([]);
      setPagination(null);
    }
  }, []);

  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (!value.trim()) {
      setSearching(false);
      setCurrentPage(1);
      fetchUsers(1, '');
      return;
    }
    setSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      setCurrentPage(1);
      await fetchUsers(1, value);
      setSearching(false);
    }, 300);
  }, [fetchUsers]);

  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
    fetchUsers(page, searchQuery);
  }, [searchQuery, fetchUsers]);

  const handleViewUserDevices = async (userId) => {
    try {
      setLoading(true);
      const data = await deviceService.getUserDevices(userId);
      setSelectedUser(data.user);
      setSelectedUser(prevState => ({
        ...prevState,
        devices: data.devices
      }));
      setIsModalOpen(true);
    } catch (error) {
      console.error('Error fetching user devices:', error);
      toastService.error('حدث خطأ أثناء جلب بيانات الأجهزة');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (userId, deviceId) => {
    setDeleteModalConfig({
      isOpen: true,
      deviceId,
      userId
    });
  };

  const handleDeleteDevice = async (userId, deviceId) => {
    try {
      // استدعاء API لحذف الجهاز
      await deviceService.deleteDevice(userId, deviceId);
      
      // تحديث حالة المكون بعد الحذف
      setUsers(users.map(user => {
        if (user.id === userId) {
          return {
            ...user,
            devicesCount: user.devicesCount - 1
          };
        }
        return user;
      }));
      
      // إذا كان المستخدم محدداً، قم بتحديث قائمة أجهزته
      if (selectedUser && selectedUser.id === userId) {
        setSelectedUser({
          ...selectedUser,
          devices: selectedUser.devices.filter(device => device.id !== deviceId),
          devicesCount: selectedUser.devicesCount - 1
        });
      }

      toastService.success('تم حذف الجهاز بنجاح');
      setDeleteModalConfig({ isOpen: false, deviceId: null, userId: null });
    } catch (error) {
      console.error('Error deleting device:', error);
      toastService.error('حدث خطأ أثناء حذف الجهاز');
    }
  };

  const exportToExcel = (fromModal = false) => {
    // تصدير بيانات من المودال (أجهزة مستخدم محدد)
    if (fromModal) {
      if (!selectedUser || !selectedUser.devices || selectedUser.devices.length === 0) {
        toastService.error('لا توجد بيانات للتصدير');
        return;
      }

      const data = selectedUser.devices.map(device => ({
        'اسم المستخدم': selectedUser.name,
        'المنطقة': device.region,
        'نظام التشغيل': device.os,
        'المتصفح': device.browser,
        'وقت الدخول': device.loginTime ? formatDateTime(device.loginTime) : '--',
        'وقت الخروج': device.logoutTime ? formatDateTime(device.logoutTime) : '--',
        'آخر استخدام': formatDateTime(device.lastUsed)
      }));

      const ws = XLSX.utils.json_to_sheet(data);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, `${selectedUser.name}_devices`);
      XLSX.writeFile(wb, `${selectedUser.name}_devices.xlsx`);
      toastService.success('تم تصدير البيانات بنجاح');
    } 
    // تصدير بيانات من الصفحة الرئيسية (جميع المستخدمين)
    else {
      // جلب جميع البيانات للتصدير
      const exportAll = async () => {
        try {
          const allData = await deviceService.getAllUsers();
          const exportData = allData.map(user => ({
            'اسم المستخدم': user.name,
            'عدد الأجهزة': user.devicesCount,
            'الحد الأقصى': user.maxDevices,
            'آخر نشاط': formatDate(user.lastActivity)
          }));

          const ws = XLSX.utils.json_to_sheet(exportData);
          const wb = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(wb, ws, "User Devices");
          XLSX.writeFile(wb, "user_devices.xlsx");
          toastService.success('تم تصدير البيانات بنجاح');
        } catch {
          toastService.error('حدث خطأ أثناء تصدير البيانات');
        }
      };
      exportAll();
    }
  };

  // تم إزالة دالة تصدير PDF

  // إذا كان هناك تحميل، عرض الشكلتون
  if (loading) {
    return <UserDevicesSkeleton />;
  }
  
  // إذا كان هناك خطأ، عرض رسالة الخطأ
  if (error) {
    return (
      <div className="p-6 text-center">
        <div className="text-red-500 text-xl mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="font-bold">حدث خطأ</h3>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
        <button 
          onClick={() => window.location.reload()} 
          className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
        >
          إعادة المحاولة
        </button>
      </div>
    );
  }
  
  const totalPages = pagination?.last_page || 0;

  return (
    <div className="space-y-6 p-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-blue-100 dark:bg-blue-900 rounded-full">
              <FaUsers className="text-blue-600 dark:text-blue-300 text-xl" />
            </div>
            <div>
              <h3 className="text-gray-500 dark:text-gray-400 text-sm">إجمالي المستخدمين</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_users}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-green-100 dark:bg-green-900 rounded-full">
              <FaLaptop className="text-green-600 dark:text-green-300 text-xl" />
            </div>
            <div>
              <h3 className="text-gray-500 dark:text-gray-400 text-sm">إجمالي الأجهزة</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.total_devices}</p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-purple-100 dark:bg-purple-900 rounded-full">
              <FaClock className="text-purple-600 dark:text-purple-300 text-xl" />
            </div>
            <div>
              <h3 className="text-gray-500 dark:text-gray-400 text-sm">متوسط الأجهزة لكل مستخدم</h3>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{stats.avg_devices_per_user}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Search Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              البحث عن مستخدم
            </label>
            <div className="relative rounded-lg">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="input-base pr-10"
                placeholder="البحث..."
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                {searching ? (
                  <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                ) : (
                  <SearchIcon className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                )}
              </div>
            </div>
          </div>

          {/* Username Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              اسم المستخدم
            </label>
            <select className="input-base">
              <option value="">الكل</option>
              {users.map((user, index) => (
                <option key={index} value={user.name}>{user.name}</option>
              ))}
            </select>
          </div>

          {/* Operating System Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              نظام التشغيل
            </label>
            <select className="input-base">
              <option value="">الكل</option>
              <option value="windows">Windows</option>
              <option value="mac">MacOS</option>
              <option value="linux">Linux</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-gray-800 shadow-sm rounded-lg">
        <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
          <h1 className="text-lg font-medium text-gray-900 dark:text-white">أجهزة المستخدمين</h1>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-800 dark:bg-gray-700">
              <tr>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                  اسم المستخدم
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                  عدد الأجهزة
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                  آخر نشاط
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                  الإجراءات
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {users.length === 0 ? (
                <tr>
                  <td colSpan="4" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                    <div className="flex flex-col items-center justify-center">
                      <svg className="w-12 h-12 mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                      </svg>
                      <p className="text-lg font-medium">لا توجد بيانات حالية للعرض</p>
                      <p className="mt-2">جرب تغيير معايير البحث أو التصفية</p>
                    </div>
                  </td>
                </tr>
              ) : (
                users.map((user) => (
                  <tr key={user.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {user.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <span className={user.devicesCount >= user.maxDevices ? 'text-red-600 dark:text-red-400' : ''}>
                        {user.devicesCount} / {user.maxDevices}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {formatDate(user.lastActivity)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      <button
                        onClick={() => handleViewUserDevices(user.id)}
                        className="text-blue-600 hover:text-blue-700 dark:text-blue-500 dark:hover:text-blue-400"
                        title="عرض الأجهزة"
                      >
                        <HiOutlineEye className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination controls */}
        <div className="flex items-center justify-center bg-gray-50 dark:bg-gray-700 px-4 py-3 border-t border-gray-200 dark:border-gray-600 rounded-b-lg">
          <div className="flex items-center">
            <button 
              className="p-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
            >
              <ChevronRightIcon className="h-5 w-5" />
            </button>
            <span className="mx-3 text-sm font-medium text-gray-700 dark:text-gray-300">
              {currentPage} / {totalPages}
            </span>
            <button 
              className="p-2 text-blue-600 hover:text-blue-800 transition-colors duration-200"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
            >
              <ChevronLeftIcon className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Devices Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => setIsModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/25 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-6xl transform overflow-visible rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl transition-all">
                  <div className="flex justify-between items-center mb-4">
                    <Dialog.Title className="text-lg font-medium text-gray-900 dark:text-white">
                      {selectedUser?.name}
                    </Dialog.Title>
                    <div className="flex items-center gap-4">
                      <button
                        onClick={() => exportToExcel(true)}
                        className="p-2 text-green-600 hover:text-green-700 dark:text-green-500 dark:hover:text-green-400"
                        title="تصدير إلى Excel"
                      >
                        <FaFileExcel className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  {/* Devices Table */}
                  {selectedUser?.devices && selectedUser.devices.length > 0 && (
                    <div className="mt-6">
                      <div className="overflow-x-auto">
                        <table className="w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <colgroup>
                            <col className="w-[15%]" /> {/* اسم الجهاز */}
                            <col className="w-[10%]" /> {/* المنطقة */}
                            <col className="w-[15%]" /> {/* نظام التشغيل */}
                            <col className="w-[10%]" /> {/* المتصفح */}
                            <col className="w-[15%]" /> {/* وقت الدخول */}
                            <col className="w-[15%]" /> {/* وقت الخروج */}
                            <col className="w-[15%]" /> {/* آخر استخدام */}
                            <col className="w-[5%]" />  {/* الإجراءات */}
                          </colgroup>
                          <thead className="bg-gray-800 dark:bg-gray-700">
                            <tr>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                اسم المستخدم
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                المنطقة
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                نظام التشغيل
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                المتصفح
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                وقت الدخول
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                وقت الخروج
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                آخر استخدام
                              </th>
                              <th scope="col" className="px-3 py-3 text-right text-xs font-medium text-white dark:text-white uppercase tracking-wider">
                                الإجراءات
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {selectedUser?.devices.map((device, index) => {
                              // تحقق ما إذا كان الجهاز من منطقة مختلفة عن باقي أجهزة المستخدم
                              const isFromDifferentRegion = selectedUser.devices.some(d => 
                                d.id !== device.id && d.region !== device.region
                              );

                              return (
                                <tr key={device.id} className={
                                  isFromDifferentRegion
                                    ? 'bg-red-50 dark:bg-red-900' 
                                    : index % 2 === 0 
                                      ? 'bg-white dark:bg-gray-800' 
                                      : 'bg-gray-50 dark:bg-gray-800'
                                }>
                                  <td className="px-3 py-2 text-sm text-gray-900 dark:text-white whitespace-nowrap">
                                    {selectedUser.name}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                    {device.region}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                    {device.os}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-900 dark:text-white">
                                    {device.browser}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-900 dark:text-white font-mono text-center">
                                    {formatDateTime(device.loginTime || '--')}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-900 dark:text-white font-mono text-center">
                                    {formatDateTime(device.logoutTime || '--')}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-900 dark:text-white font-mono text-center">
                                    {formatDateTime(device.lastUsed)}
                                  </td>
                                  <td className="px-3 py-2 text-sm text-gray-900 dark:text-white text-center">
                                    <div className="flex justify-center">
                                      {hasPermission && hasPermission('delete_devices') && (
                                        <button
                                          onClick={() => handleDeleteClick(selectedUser.id, device.id)}
                                          className="text-red-600 hover:text-red-700 dark:text-red-500 dark:hover:text-red-400"
                                          title="حذف الجهاز"
                                        >
                                          <HiOutlineTrash className="h-5 w-5" />
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot className="bg-gray-800 dark:bg-gray-700">
                            <tr>
                              <td colSpan="9" className="px-3 py-3">
                                <div className="flex justify-between items-center">
                                  <div className="text-sm text-white dark:text-white">
                                    إجمالي الأجهزة: {selectedUser?.devices.length || 0}
                                  </div>
                                  <div className="flex gap-4 text-sm text-white dark:text-white">
                                    <span>Windows: {selectedUser?.devices.filter(d => d.os.toLowerCase().includes('windows')).length || 0}</span>
                                    <span>Mac: {selectedUser?.devices.filter(d => d.os.toLowerCase().includes('mac')).length || 0}</span>
                                    <span>iOS: {selectedUser?.devices.filter(d => d.os.toLowerCase().includes('ios')).length || 0}</span>
                                    <span>Android: {selectedUser?.devices.filter(d => d.os.toLowerCase().includes('android')).length || 0}</span>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    </div>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={deleteModalConfig.isOpen}
        onClose={() => setDeleteModalConfig({ isOpen: false, deviceId: null, userId: null })}
        onConfirm={() => handleDeleteDevice(deleteModalConfig.userId, deleteModalConfig.deviceId)}
        itemType="الجهاز"
        customMessage="هل أنت متأكد من حذف هذا الجهاز؟"
      />
    </div>
  );
};

export default UserDevices;
